import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import ApiClient from '../../utils/ApiClient';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import { getFirstAttributeValue, getUserAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { normalizeUserTypeCode } from '../../utils/UserTypeUtils.js';

const extractSupervisorIdFromUser = (user) => {
    return String(
        user?.supervisor ??
        user?.supervisorUuid ??
        user?.supervisorId ??
        getUserAttributeValue(user, ['supervisor', 'Supervisor']) ??
        ''
    ).trim();
};

const extractUserTypeFromUser = (user) => {
    return normalizeUserTypeCode(
        user?.UserType ??
        user?.userType ??
        user?.user_type ??
        getUserAttributeValue(user, ['UserType', 'userType']) ??
        ''
    );
};

const extractUserTypeFromToken = (tokenDetails) => {
    return normalizeUserTypeCode(
        getFirstAttributeValue(
            tokenDetails,
            ['UserType', 'userType'],
            getFirstAttributeValue(tokenDetails?.attributes, ['UserType', 'userType'], '')
        )
    );
};

const HomePage = ({ authDetails, onSignOut }) => {
    const [isSessionDetailsOpen, setIsSessionDetailsOpen] = useState(false);
    const [approvalTaskCount, setApprovalTaskCount] = useState(0);
    const [approvalHasOverdue, setApprovalHasOverdue] = useState(false);
    const [isLoadingApprovalTasks, setIsLoadingApprovalTasks] = useState(true);
    const tokenDetails = authDetails?.identity?.tokenParsed ?? {};
    const tokenSupervisorId = String(tokenDetails.supervisor ?? '').trim();
    const tokenUserType = extractUserTypeFromToken(tokenDetails);
    const [resolvedSupervisorId, setResolvedSupervisorId] = useState(tokenSupervisorId);
    const [resolvedUserType, setResolvedUserType] = useState(tokenUserType);
    const subject = String(tokenDetails?.sub ?? authDetails?.identity?.subject ?? '').trim();
    const isSupervisorMissing = !String(resolvedSupervisorId).trim();
    const isUserTypeMissing = !String(resolvedUserType).trim();
    const userUuid =
        tokenDetails?.sub ?? tokenDetails?.preferred_username ?? tokenDetails?.email ?? '';

    const handleSessionDetailsToggle = () => {
        setIsSessionDetailsOpen((prev) => !prev);
    };

    useEffect(() => {
        setResolvedSupervisorId(tokenSupervisorId);
    }, [tokenSupervisorId]);

    useEffect(() => {
        setResolvedUserType(tokenUserType);
    }, [tokenUserType]);

    useEffect(() => {
        let isMounted = true;

        if ((tokenSupervisorId && tokenUserType) || !subject) {
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorFromProfile = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const supervisorId = extractSupervisorIdFromUser(response.data ?? {});
                    if (supervisorId) {
                        setResolvedSupervisorId(supervisorId);
                    }
                    const userType = extractUserTypeFromUser(response.data ?? {});
                    if (userType) {
                        setResolvedUserType(userType);
                    }
                }
            } catch (error) {
                // No-op. If supervisor cannot be resolved, existing warning behavior remains.
            }
        };

        fetchSupervisorFromProfile();

        return () => {
            isMounted = false;
        };
    }, [subject, tokenSupervisorId, tokenUserType]);

    useEffect(() => {
        let isMounted = true;
        const resolvedUserUuid = String(userUuid || '').trim();

        if (!resolvedUserUuid) {
            setApprovalTaskCount(0);
            setApprovalHasOverdue(false);
            return () => {
                isMounted = false;
            };
        }

        const normalizeGroupId = (group) => {
            if (!group) {
                return null;
            }
            if (typeof group === 'string' || typeof group === 'number') {
                return String(group).trim();
            }
            if (group.id || group.uuid) {
                return String(group.id ?? group.uuid).trim();
            }
            return null;
        };

        const normalizeSubmittedAt = (value) => {
            if (!value) {
                return null;
            }
            if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : value;
            }
            const rawValue = String(value).trim();
            if (!rawValue) {
                return null;
            }
            const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(rawValue);
            const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
            const parsed = new Date(normalizedValue);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
            const fallback = new Date(rawValue);
            return Number.isNaN(fallback.getTime()) ? null : fallback;
        };

        const isApprovalOverdue = (request) => {
            const submittedAt = normalizeSubmittedAt(request?.createdAt ?? '');
            if (!submittedAt) {
                return false;
            }
            const diffMs = Date.now() - submittedAt.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            return diffDays > 15;
        };

        const extractRequestList = (responseData) => {
            if (Array.isArray(responseData)) {
                return responseData;
            }
            return responseData?.content ?? responseData?.requests ?? [];
        };

        const extractReviewList = (responseData) => {
            if (Array.isArray(responseData)) {
                return responseData;
            }
            return responseData?.content ?? responseData?.reviews ?? [];
        };

        const isFinalReviewDecision = (decision) => {
            const normalizedDecision = String(decision ?? '').trim().toUpperCase();
            return Boolean(normalizedDecision) && normalizedDecision !== 'DRAFT';
        };

        const getLatestReviewByType = (reviews, reviewType) => {
            const normalizedType = String(reviewType ?? '').trim().toUpperCase();
            const reviewList = Array.isArray(reviews) ? reviews : [];

            const candidates = reviewList.filter((review) => {
                const candidateType = String(review?.type ?? '').trim().toUpperCase();
                return candidateType === normalizedType && isFinalReviewDecision(review?.decision);
            });

            if (candidates.length === 0) {
                return null;
            }

            const resolveCreatedAt = (review) => {
                const parsed = normalizeSubmittedAt(review?.createdAt ?? review?.updatedAt ?? null);
                return parsed ? parsed.getTime() : Number.NEGATIVE_INFINITY;
            };

            return candidates.sort((left, right) => {
                const createdAtDelta = resolveCreatedAt(right) - resolveCreatedAt(left);
                if (createdAtDelta !== 0) {
                    return createdAtDelta;
                }

                const leftId = Number(left?.id ?? 0);
                const rightId = Number(right?.id ?? 0);
                return rightId - leftId;
            })[0];
        };

        const enrichRequestsWithSupervisorReview = async (requests) => {
            const requestList = Array.isArray(requests) ? requests : [];

            return Promise.all(
                requestList.map(async (request) => {
                    if (request?.supervisorReview?.decision) {
                        return request;
                    }

                    const requestId = String(request?.id ?? '').trim();
                    if (!requestId) {
                        return request;
                    }

                    try {
                        const reviewResponse = await ApiClient.get(
                            `/access-requests/${encodeURIComponent(requestId)}/review`
                        );

                        if (reviewResponse.status !== 200) {
                            return request;
                        }

                        const reviewList = extractReviewList(reviewResponse.data ?? {});
                        const supervisorReview = getLatestReviewByType(reviewList, 'SUPERVISOR');

                        if (!supervisorReview) {
                            return request;
                        }

                        return {
                            ...request,
                            supervisorReview,
                        };
                    } catch (error) {
                        return request;
                    }
                })
            );
        };

        const tokenApproverGroupNames = (authDetails?.identity?.tokenParsed?.groups ?? [])
            .filter((group) => typeof group === 'string')
            .map((group) => group.trim())
            .filter((group) => group.endsWith('-Approvers'));

        const fetchApproverGroups = async () => {
            const fallbackGroups = tokenApproverGroupNames.map((name) => ({
                id: '',
                name,
            }));

            if (!subject) {
                return fallbackGroups;
            }

            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}/groups`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const groups = Array.isArray(responseData)
                    ? responseData
                    : responseData.content ?? responseData.groups ?? [];
                const filteredGroups = (Array.isArray(groups) ? groups : [])
                    .map((group) => {
                        const name = String(
                            group?.name ?? group?.displayName ?? group?.id ?? group?.uuid ?? ''
                        ).trim();
                        return {
                            id: String(normalizeGroupId(group) ?? ''),
                            name,
                        };
                    })
                    .filter((group) => group.name.endsWith('-Approvers'));

                return filteredGroups.length > 0 ? filteredGroups : fallbackGroups;
            } catch (error) {
                return fallbackGroups;
            }
        };

        const fetchApprovalTasks = async () => {
            setIsLoadingApprovalTasks(true);
            try {
                const [supervisorResponse, approverGroups] = await Promise.all([
                    ApiClient.get('/access-requests', {
                        params: {
                            page: 0,
                            size: 100,
                            supervisor: resolvedUserUuid,
                            state: 'SUBMITTED',
                        },
                    }),
                    fetchApproverGroups(),
                ]);

                if (supervisorResponse.status !== 200) {
                    throw new Error(`API responded with ${supervisorResponse.status}`);
                }

                const supervisorResponseData = supervisorResponse.data ?? {};
                const supervisorRequestList = extractRequestList(supervisorResponseData);
                const supervisorTasks = (Array.isArray(supervisorRequestList) ? supervisorRequestList : []).filter(
                    (request) => {
                        const status = String(request?.state ?? request?.status ?? '').toUpperCase();
                        const requestSupervisor =
                            request?.supervisorUuid ??
                            request?.supervisorId ??
                            request?.supervisorUserId ??
                            request?.supervisorUserUuid ??
                            request?.supervisor ??
                            '';
                        return status === 'SUBMITTED' && String(requestSupervisor).trim() === resolvedUserUuid;
                    }
                );

                const entitlementOwnerGroupUuidParam = Array.from(
                    new Set(
                        approverGroups
                            .map((group) => String(group?.id ?? '').trim())
                            .filter(Boolean)
                    )
                ).join(',');
                let entitlementOwnerTasks = [];

                if (entitlementOwnerGroupUuidParam) {
                    const entitlementOwnerResponse = await ApiClient.get('/access-requests', {
                        params: {
                            page: 0,
                            size: 100,
                            entitlementOwner: entitlementOwnerGroupUuidParam,
                        },
                    });

                    if (entitlementOwnerResponse.status !== 200) {
                        throw new Error(`API responded with ${entitlementOwnerResponse.status}`);
                    }

                    const entitlementOwnerResponseData = entitlementOwnerResponse.data ?? {};
                    const entitlementOwnerRequestList = extractRequestList(entitlementOwnerResponseData);
                    const requestsWithReviewState = await enrichRequestsWithSupervisorReview(
                        Array.isArray(entitlementOwnerRequestList) ? entitlementOwnerRequestList : []
                    );
                    entitlementOwnerTasks = requestsWithReviewState.filter((request) => {
                        const status = String(request?.state ?? request?.status ?? '').toUpperCase();
                        const supervisorDecision = String(
                            request?.supervisorReview?.decision ?? ''
                        ).toUpperCase();
                        return status === 'IN_PROGRESS' && supervisorDecision === 'APPROVED';
                    });
                }

                const total = supervisorTasks.length + entitlementOwnerTasks.length;
                const hasOverdue = [...supervisorTasks, ...entitlementOwnerTasks].some((request) =>
                    isApprovalOverdue(request)
                );

                if (isMounted) {
                    setApprovalTaskCount(total);
                    setApprovalHasOverdue(hasOverdue);
                }
            } catch (error) {
                if (isMounted) {
                    setApprovalTaskCount(0);
                    setApprovalHasOverdue(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingApprovalTasks(false);
                }
            }
        };

        fetchApprovalTasks();

        return () => {
            isMounted = false;
        };
    }, [authDetails, subject, userUuid]);

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    {isSupervisorMissing ? (
                        <div className="grid-row grid-gap home-alert-row">
                            <div className="tablet:grid-col-12">
                                <div className="usa-alert usa-alert--warning" role="alert">
                                    <div className="usa-alert__body">
                                        <h3 className="usa-alert__heading">Supervisor needed to request access</h3>
                                        <p className="usa-alert__text">
                                            Add your supervisor in your profile to request access. Until a supervisor is
                                            set, you will not be able to submit access requests. Visit{' '}
                                            <Link to="/profile">Update profile</Link> to complete this step.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {isUserTypeMissing ? (
                        <div className="grid-row grid-gap home-alert-row">
                            <div className="tablet:grid-col-12">
                                <div className="usa-alert usa-alert--warning" role="alert">
                                    <div className="usa-alert__body">
                                        <h3 className="usa-alert__heading">User type needed to request access</h3>
                                        <p className="usa-alert__text">
                                            Select your user type in your profile to request access. Until user type is
                                            set, you will not be able to submit access requests. Visit{' '}
                                            <Link to="/profile">Update profile</Link> to complete this step.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Create a new access request</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <p className="usa-prose">
                                            Start a new access request for a JCWA system.
                                        </p>
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button" to="/requests">
                                            Start request
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Current accesses granted</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <p className="usa-prose">
                                            View the access you already have across JCWA systems.
                                        </p>
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button usa-button--outline" to="/user">
                                            View access
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Recertifications due</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <div className="usa-prose">
                                            <div className="usa-display">0</div>
                                            <p>You currently have recertifications due.</p>
                                        </div>
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button usa-button--outline" to="/recertifications">
                                            View my recertifications
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">
                                            Approval tasks{' '}
                                            {approvalHasOverdue ? (
                                                <span className="usa-tag usa-tag--overdue">Overdue</span>
                                            ) : null}
                                        </h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <div className="usa-prose">
                                            <div className="usa-display">
                                                {isLoadingApprovalTasks ? (
                                                    <ValueWithSkeleton loading type="heading" width="3ch" />
                                                ) : (
                                                    approvalTaskCount
                                                )}
                                            </div>
                                            <p>Approval tasks are waiting for you.</p>
                                        </div>
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button usa-button--outline" to="/approvals">
                                            View approval tasks
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">
                                            Keycloak session details <span className="usa-tag">Debug info</span>
                                        </h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <div className="usa-accordion usa-accordion--borderless">
                                            <h3 className="usa-accordion__heading">
                                                <button
                                                    type="button"
                                                    className="usa-accordion__button"
                                                    aria-expanded={isSessionDetailsOpen}
                                                    aria-controls="session-details"
                                                    onClick={handleSessionDetailsToggle}
                                                >
                                                    View session details
                                                </button>
                                            </h3>
                                            <div
                                                id="session-details"
                                                className="usa-accordion__content usa-prose"
                                                hidden={!isSessionDetailsOpen}
                                            >
                                                <p>
                                                    You are signed in. Below are the values currently stored
                                                    after your login, including cookies and identity-related
                                                    fields from Keycloak.
                                                </p>
                                                <div className="usa-alert usa-alert--info" role="status">
                                                    <div className="usa-alert__body">
                                                        <p className="usa-alert__text">
                                                            Keep this information private. It may contain sensitive
                                                            access details.
                                                        </p>
                                                    </div>
                                                </div>
                                                <h4>Identity information</h4>
                                                <pre className="usa-code">
                                                    {JSON.stringify(authDetails.identity, null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default HomePage;
